import React, { useEffect, useState } from 'react';
import { Sparkles, TrendingUp, Target, Loader2 } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact AI pricing insight strip for the QuotePreview.
 * Re-fetches (debounced) when project_type / total / markup change.
 */
const AIPricingInsight = ({ projectType, total, markupPercent, squareFeet, onApplyMarkup }) => {
  const debTotal = useDebounce(total, 500);
  const debMarkup = useDebounce(markupPercent, 500);
  const debType = useDebounce(projectType, 500);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!debType || !debTotal || debTotal <= 0) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(
          `${API}/ai/pricing-assistant`,
          {
            project_type: debType,
            total: Number(debTotal),
            markup_percent: Number(debMarkup) || 0,
            square_feet: squareFeet ? Number(squareFeet) : null,
          },
          { withCredentials: true, timeout: 20000 }
        );
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [debType, debTotal, debMarkup, squareFeet]);

  if (!data && !loading) return null;

  const winColor =
    !data ? 'text-slate-300' :
    data.win_probability >= 0.7 ? 'text-green-400' :
    data.win_probability >= 0.45 ? 'text-amber-400' :
    'text-red-400';

  return (
    <div className="rounded-lg border border-blue-500/40 bg-gradient-to-br from-blue-950/60 to-slate-950/60 p-4 space-y-3" data-testid="ai-pricing-insight">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-300" />
          <span className="text-sm font-semibold text-blue-100">AI Pricing Insight</span>
        </div>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-300" />}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-900/60 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <TrendingUp className="w-3.5 h-3.5" /> Suggested markup
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-2xl font-bold text-white" data-testid="ai-insight-markup">
                  {data.recommended_markup_percent}%
                </span>
                {onApplyMarkup && Number(markupPercent) !== Number(data.recommended_markup_percent) && (
                  <button
                    type="button"
                    onClick={() => onApplyMarkup(data.recommended_markup_percent)}
                    className="text-xs text-blue-300 hover:text-blue-200 underline"
                    data-testid="ai-insight-apply-markup"
                  >
                    apply
                  </button>
                )}
              </div>
            </div>
            <div className="bg-slate-900/60 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <Target className="w-3.5 h-3.5" /> Win probability
              </div>
              <span className={`text-2xl font-bold ${winColor}`} data-testid="ai-insight-win">
                {Math.round(data.win_probability * 100)}%
              </span>
            </div>
          </div>
          <p className="text-xs text-slate-300 leading-relaxed" data-testid="ai-insight-reasoning">
            {data.reasoning}
          </p>
          {!data.has_history && (
            <p className="text-[10px] text-slate-500 italic">
              Suggestions improve as you close more quotes.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default AIPricingInsight;
